import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './LoginPage.css';
import { setAuthTokens } from '../utils/api';

interface LoginPageProps {
  onSwitchToRegister: () => void;
  onLoginSuccess: (accessToken: string, refreshToken: string, username: string, isFirstLogin: boolean) => void;
}

// Rate limiting constants
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 60000; // 1 minute in ms
const ATTEMPT_RESET_TIME = 300000; // 5 minutes in ms

interface TerminalLine {
  text: string | React.ReactNode;
  type: 'comment' | 'code' | 'success' | 'error' | 'output';
  isTyping?: boolean;
  id?: string;
}

// Helper to create syntax-highlighted Lua code
const highlightLua = (code: string, isTyping?: boolean): React.ReactNode => {
  // Match patterns for different syntax elements
  const patterns = [
    { regex: /(local|require|function|end|return|if|then|else|elseif|for|while|do|nil|true|false)\b/g, className: 'lua-keyword' },
    { regex: /("(?:[^"\\]|\\.)*")/g, className: 'lua-string' },
    { regex: /(\d+)/g, className: 'lua-number' },
    { regex: /(credentials|VoltAuth|response)(\.[a-zA-Z_]+)?/g, className: 'lua-variable' },
    { regex: /(=|:)/g, className: 'lua-operator' },
  ];
  
  // Simple tokenization approach
  const parts: { text: string; className?: string }[] = [];
  let remaining = code;
  let lastIndex = 0;
  
  // Create a combined regex for all patterns
  const keywordRegex = /\b(local|require|function|end|return|if|then|else|elseif|for|while|do|nil|true|false)\b/g;
  const stringRegex = /"(?:[^"\\]|\\.)*"/g;
  
  // Find all matches
  interface Match {
    index: number;
    text: string;
    className: string;
  }
  const matches: Match[] = [];
  
  let match;
  while ((match = keywordRegex.exec(code)) !== null) {
    matches.push({ index: match.index, text: match[0], className: 'lua-keyword' });
  }
  while ((match = stringRegex.exec(code)) !== null) {
    matches.push({ index: match.index, text: match[0], className: 'lua-string' });
  }
  
  // Sort by index
  matches.sort((a, b) => a.index - b.index);
  
  // Build parts
  let currentIndex = 0;
  for (const m of matches) {
    if (m.index > currentIndex) {
      parts.push({ text: code.slice(currentIndex, m.index) });
    }
    parts.push({ text: m.text, className: m.className });
    currentIndex = m.index + m.text.length;
  }
  if (currentIndex < code.length) {
    parts.push({ text: code.slice(currentIndex) });
  }
  
  return (
    <>
      {parts.map((part, i) => (
        part.className ? (
          <span key={i} className={part.className}>{part.text}</span>
        ) : (
          <span key={i}>{part.text}</span>
        )
      ))}
      {isTyping && <span className="terminal-cursor">_</span>}
    </>
  );
};

const LoginPage: React.FC<LoginPageProps> = ({ onSwitchToRegister, onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Rate limiting state
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutEndTime, setLockoutEndTime] = useState<number | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const [firstAttemptTime, setFirstAttemptTime] = useState<number | null>(null);
  
  // Lockout countdown timer
  useEffect(() => {
    if (lockoutEndTime) {
      const interval = setInterval(() => {
        const remaining = Math.max(0, lockoutEndTime - Date.now());
        setLockoutRemaining(remaining);
        if (remaining === 0) {
          setLockoutEndTime(null);
          setLoginAttempts(0);
          setFirstAttemptTime(null);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [lockoutEndTime]);
  
  // Reset attempts after ATTEMPT_RESET_TIME
  useEffect(() => {
    if (firstAttemptTime && !lockoutEndTime) {
      const timeout = setTimeout(() => {
        setLoginAttempts(0);
        setFirstAttemptTime(null);
      }, ATTEMPT_RESET_TIME);
      return () => clearTimeout(timeout);
    }
  }, [firstAttemptTime, lockoutEndTime]);
  
  // Static/historical lines that don't change
  const [historyLines, setHistoryLines] = useState<TerminalLine[]>([
    { text: '-- Volt Authentication Module v1.1.0', type: 'comment' },
    { text: 'local VoltAuth = require("@volt/auth")', type: 'code' },
    { text: 'local credentials = {}', type: 'code' },
    { text: '', type: 'code' },
  ]);
  
  const [authResult, setAuthResult] = useState<'success' | 'error' | null>(null);
  const [typingUsername, setTypingUsername] = useState('');
  const [typingPassword, setTypingPassword] = useState('');
  const [focusedInput, setFocusedInput] = useState<'username' | 'password' | null>(null);

  // Typing effect for username
  useEffect(() => {
    if (username.length > typingUsername.length) {
      const timeout = setTimeout(() => {
        setTypingUsername(username.slice(0, typingUsername.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (username.length < typingUsername.length) {
      setTypingUsername(username);
    }
  }, [username, typingUsername]);

  // Typing effect for password
  useEffect(() => {
    if (password.length > typingPassword.length) {
      const timeout = setTimeout(() => {
        setTypingPassword(password.slice(0, typingPassword.length + 1));
      }, 30);
      return () => clearTimeout(timeout);
    } else if (password.length < typingPassword.length) {
      setTypingPassword(password);
    }
  }, [password, typingPassword]);

  // Compute terminal lines: history + live credential lines
  const terminalLines = useMemo(() => {
    const lines: TerminalLine[] = [...historyLines];
    
    // Add username line only if there's content
    if (typingUsername) {
      lines.push({
        text: `credentials.username = "${typingUsername}"`,
        type: 'code',
        isTyping: focusedInput === 'username',
        id: 'username-line'
      });
    }
    
    // Add password line only if there's content
    if (typingPassword) {
      const masked = '*'.repeat(typingPassword.length);
      lines.push({
        text: `credentials.password = "${masked}"`,
        type: 'code',
        isTyping: focusedInput === 'password',
        id: 'password-line'
      });
    }
    
    return lines;
  }, [historyLines, typingUsername, typingPassword, focusedInput]);

  const addTerminalLine = (line: TerminalLine) => {
    setHistoryLines(prev => [...prev, line]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if locked out
    if (lockoutEndTime && Date.now() < lockoutEndTime) {
      const seconds = Math.ceil(lockoutRemaining / 1000);
      setValidationError(`Too many attempts. Please wait ${seconds} seconds.`);
      return;
    }
    
    // Custom validation
    if (!username.trim()) {
      setValidationError('Please enter your username or email');
      return;
    }
    if (!password) {
      setValidationError('Please enter your password');
      return;
    }
    
    // Track first attempt time for reset
    if (!firstAttemptTime) {
      setFirstAttemptTime(Date.now());
    }
    
    // Save credentials before clearing
    const savedUsername = username;
    const savedPassword = password;
    
    setIsLoading(true);
    setAuthResult(null);
    setValidationError(null);
    
    // Freeze the current credentials into history before showing login attempt
    if (typingUsername) {
      addTerminalLine({ text: `credentials.username = "${typingUsername}"`, type: 'code' });
    }
    if (typingPassword) {
      const masked = '*'.repeat(typingPassword.length);
      addTerminalLine({ text: `credentials.password = "${masked}"`, type: 'code' });
    }
    
    addTerminalLine({ text: '', type: 'code' });
    addTerminalLine({ text: '-- Initiating authentication...', type: 'comment' });
    addTerminalLine({ text: 'local response = VoltAuth:Login(credentials)', type: 'code' });
    
    // Clear input fields now that they're in history
    setUsername('');
    setPassword('');
    setTypingUsername('');
    setTypingPassword('');
    
    try {
      const response = await fetch('https://api.volt.bz/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: savedUsername, password: savedPassword }),
      });
      
      const data = await response.json();
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (response.ok) {
        // API returns { success: true, data: { accessToken, refreshToken } }
        const tokenData = data.data || data;
        const accessToken = tokenData.accessToken || tokenData.access_token || tokenData.token;
        const refreshToken = tokenData.refreshToken || tokenData.refresh_token;
        
        // Store in both memory and localStorage via api module
        setAuthTokens(accessToken, refreshToken);
        localStorage.setItem('lastActivity', Date.now().toString());
        localStorage.setItem('username', savedUsername);
        
        // Check if first login (no tutorial completed flag)
        const hasCompletedTutorial = localStorage.getItem('tutorialCompleted') === 'true';
        
        addTerminalLine({ text: '', type: 'code' });
        addTerminalLine({ text: 'print(response.status)', type: 'code' });
        addTerminalLine({ text: '>> SUCCESS', type: 'success' });
        addTerminalLine({ text: '-- Access granted. Redirecting...', type: 'comment' });
        setAuthResult('success');
        
        // Reset rate limiting on success
        setLoginAttempts(0);
        setFirstAttemptTime(null);
        
        // Delay redirect to show success message
        setTimeout(() => {
          onLoginSuccess(accessToken, refreshToken, savedUsername, !hasCompletedTutorial);
        }, 1500);
      } else {
        // Increment login attempts
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        // Check if should lockout
        if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
          setLockoutEndTime(Date.now() + LOCKOUT_DURATION);
          addTerminalLine({ text: '', type: 'code' });
          addTerminalLine({ text: 'print(response.error)', type: 'code' });
          addTerminalLine({ text: `>> ERROR: Too many failed attempts. Locked for 60 seconds.`, type: 'error' });
          addTerminalLine({ text: '-- Rate limit exceeded.', type: 'comment' });
        } else {
          addTerminalLine({ text: '', type: 'code' });
          addTerminalLine({ text: 'print(response.error)', type: 'code' });
          addTerminalLine({ text: `>> ERROR: ${data.message || 'Invalid credentials'}`, type: 'error' });
          addTerminalLine({ text: `-- Authentication failed. ${MAX_LOGIN_ATTEMPTS - newAttempts} attempts remaining.`, type: 'comment' });
        }
        addTerminalLine({ text: '', type: 'code' });
        addTerminalLine({ text: 'credentials = {}', type: 'code' });
        setAuthResult('error');
      }
    } catch (error) {
      addTerminalLine({ text: '', type: 'code' });
      addTerminalLine({ text: '>> ERROR: Connection failed', type: 'error' });
      addTerminalLine({ text: '-- Unable to reach authentication server.', type: 'comment' });
      addTerminalLine({ text: '', type: 'code' });
      addTerminalLine({ text: 'credentials = {}', type: 'code' });
      setAuthResult('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if login button should be disabled
  const isLocked = lockoutEndTime !== null && Date.now() < lockoutEndTime;

  return (
    <div className="login-page">
      <div className="login-layout">
        {/* Left side - Login Form */}
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <img src="/bolt-full.svg" alt="Volt Logo" className="login-logo" />
              <h1 className="login-title">Welcome to Volt</h1>
              <p className="login-subtitle">Enter your credentials to continue.</p>
            </div>

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username or Email
                </label>
                <input
                  type="text"
                  id="username"
                  className="form-input"
                  placeholder="user@volt.bz"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('username')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setValidationError(null); }}
                  onFocus={() => setFocusedInput('password')}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>

              {validationError && (
                <div className="validation-error">
                  {validationError}
                </div>
              )}
              
              {isLocked && (
                <div className="lockout-warning">
                  🔒 Too many attempts. Please wait {Math.ceil(lockoutRemaining / 1000)}s
                </div>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading || isLocked}
              >
                {isLoading ? (
                  <span className="btn-loading">
                    <span className="btn-spinner"></span>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>

              <div className="divider">
                <span>OR</span>
              </div>

              <button type="button" className="signup-btn" onClick={onSwitchToRegister}>
                Sign up
              </button>
            </form>

            <p className="terms-text">
              By continuing, you agree to our{' '}
              <a href="#terms" className="terms-link">Terms of Service</a>
              {' '}and{' '}
              <a href="#privacy" className="terms-link">Privacy Policy</a>.
            </p>
          </div>
        </div>

        {/* Right side - Terminal */}
        <div className="terminal-container">
          <div className="terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <span className="terminal-title">auth.lua</span>
            </div>
            <div className="terminal-body">
              {terminalLines.map((line, index) => (
                <div key={line.id || index} className={`terminal-line ${line.type}`}>
                  {line.type === 'output' || line.type === 'success' || line.type === 'error' ? (
                    <span>{typeof line.text === 'string' ? line.text : line.text}</span>
                  ) : (
                    <>
                      <span className="line-number">{index + 1}</span>
                      <span className="line-content">
                        {line.type === 'code' && typeof line.text === 'string' 
                          ? highlightLua(line.text, line.isTyping)
                          : (
                            <>
                              {line.text}
                              {line.isTyping && <span className="terminal-cursor">_</span>}
                            </>
                          )
                        }
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
